import { DsmPageContainer } from '@/components';
import PageHeader from '@/components/PageHeader';
import usePermissions from '@/hooks/usePermissions';
import { useGetAvlItemQuery } from '@/services/approvedVendorList';
import { PalmettoButton, PalmettoIcon } from '@palmetto/dsm-components';
import { useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { UserPermissions } from 'types';

import { EntityHistoryFeed } from '../shared/EntityHistoryFeed';

import { AllocationScheduleSection } from './AllocationScheduleSection';
import { emptyValueLabel, fieldLabel, formatHistoryValue } from './avlFieldRegistry';
import { AvlItemViewFields } from './AvlItemViewFields';
import { VendorEquipmentTypeLabels } from './equipmentTypeLabels';

const SystemField = ({ label, value }: { label: string; value?: string }) => (
  <div className="flex flex-col gap-4">
    <div className="body-300">{label}</div>
    <div className="body-bold-300 break-all">{value && value.trim() !== '' ? value : 'None'}</div>
  </div>
);

export const ApprovedVendorItemDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: item, isLoading } = useGetAvlItemQuery(id!);
  const canEditAvl = usePermissions().includes(UserPermissions.canImportAvlData);

  const handleEdit = useCallback(() => navigate(`/admin/approved-vendors/${id}/edit`), [navigate, id]);

  if (isLoading) {
    return (
      <DsmPageContainer title="Loading...">
        <div>Loading...</div>
      </DsmPageContainer>
    );
  }

  if (!item) {
    return (
      <DsmPageContainer title="Not Found">
        <div>Approved vendor item not found.</div>
      </DsmPageContainer>
    );
  }

  const heading = `${item.manufacturer} - ${item.modelNumber}`;

  return (
    <DsmPageContainer title={heading}>
      <PageHeader
        title={heading}
        eyebrow={
          <Link
            to="/admin/approved-vendors"
            className="inline-flex items-center gap-2 text-text-primary no-underline hover:underline"
          >
            <PalmettoIcon name="chevronLeft" size="sm" />
            Back to List
          </Link>
        }
      >
        {canEditAvl && (
          <PalmettoButton
            emphasis="primary"
            onClick={handleEdit}
            leadingIcon={<PalmettoIcon name="penEdit" size="sm" />}
          >
            Edit
          </PalmettoButton>
        )}
      </PageHeader>

      <div className="mb-16 grid grid-cols-3 gap-16">
        <div className="flex flex-col gap-4">
          <div className="body-300 text-text-secondary">Manufacturer</div>
          <div className="body-bold-300 text-text-primary">{item.manufacturer || emptyValueLabel}</div>
        </div>
        <div className="flex flex-col gap-4">
          <div className="body-300 text-text-secondary">Equipment Type</div>
          <div className="body-bold-300 text-text-primary">
            {VendorEquipmentTypeLabels[item.equipmentType] ?? item.equipmentType}
          </div>
        </div>
      </div>

      <div className="mb-16">
        <AvlItemViewFields item={item} />
      </div>

      <AllocationScheduleSection allocationDetails={item.allocationDetails} />

      <div className="mb-16 flex flex-col gap-4">
        <h2 className="text-left heading-200 text-text-primary">Change History</h2>
        <EntityHistoryFeed history={item.history ?? []} fieldLabel={fieldLabel} formatValue={formatHistoryValue} />
      </div>

      <div className="flex flex-col gap-4 text-text-tertiary">
        <h2 className="text-left body-bold-200">System</h2>
        <div className="grid grid-cols-2 gap-x-16 gap-y-4">
          <SystemField label="ID" value={item.id} />
          <SystemField label="Catalog Key" value={item.catalogKey} />
          <SystemField label="Legacy Catalog Key" value={item.legacyCatalogKey} />
          <SystemField label="Legacy IDs" value={item.legacyIds.length ? item.legacyIds.join(', ') : undefined} />
        </div>
      </div>
    </DsmPageContainer>
  );
};
